import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import api from "../../../api/axios";

/* ── Bulk Stock — a stock take, one store at a time ────────────────────
   The sibling of BulkAllocate, and deliberately the same shape: pick the
   scope, paste the lines, preview what matched, confirm, apply. Nothing is
   written before the confirm.

   THREE THINGS THIS SCREEN OWES THE OPERATOR
     1. A store. /admin/bulk/stock/* runs behind CountScope, so a superadmin
        that has named no store is refused with a 400 store_required. The
        picker below makes them name one BEFORE the request is fired, so that
        400 is never reached by accident. A role 2/3 admin is pinned
        server-side from the token; the pill just tells them which shelf they
        are counting.
     2. Two pools, never blurred. `packs` is a variant SKU counted in PACKS;
        `bulk` is a milling bin counted in GRAMS. Every number on screen
        carries its unit and the two are never added together — see poolTotals.
     3. A preview that visibly writes nothing, and a result that survives.

   ── ONE BATCH AT A TIME, AND EVERY BATCH SELF-CONTAINED ──
   A preview and an apply are two round trips over the same lines, and the
   operator can start the second before the first has landed. So neither is
   allowed to read the other's state:
     · every preview carries an `id` and the exact rows it was computed from;
     · every result carries a SNAPSHOT of the lines that were sent, the mode,
       the store and the id of the preview it came from.
   The failed list, the applied table and its reconciliation all read that
   snapshot, never the live `preview`. Two replies landing out of order can
   therefore no longer cross-contaminate, and clearing the form mid-write can
   no longer make a completed write unrenderable.

   ── PREVIEW GENERATION TOKEN ──
   A preview reads every Product doc, so it can take seconds. Changing the
   store or the mode invalidates it outright — and cancels it: previewSeq is
   bumped, and a reply for an abandoned generation is dropped on arrival. Left
   unchecked, that reply would repaint a full, applyable preview computed for
   the store or mode the operator had just moved away from.

   STORE SCOPING ON THE WIRE
     THE X-Store-Id HEADER IS DEAD. The new service takes the store out of the
     JSON BODY as `storeId` on both calls and reads no header at all, so the
     picker's value travels in the payload. For a store-bound admin the body's
     storeId is ignored outright — CountScope pins role 2/3 from the token —
     which is exactly the point: a scoped account cannot count someone else's
     shelf by naming it. A superadmin that names none gets a 400
     store_required, which is why the picker gates the button.             */

/* matching Store/Stores.jsx and the service's admin roles */
const SUPERADMIN_ROLE = 1;

/* mirrors MaxRows in ledger-service/Api/BulkStockEndpoints.cs — refusing here
   saves a 5,000-row round trip that was always going to come back a 400 */
const MAX_ROWS = 5000;

/* ── ERRORS OFF THE NEW SERVICE ──
   The service answers with a REAL HTTP STATUS and a { code, message } body.
   Node's "200 carrying status:400 in the payload" is gone, so nothing here
   may read a status field out of a body — and the plain refusals
   ({ code: "forbidden" }, and store_required's siblings) can arrive with no
   message at all, which is why the status has to supply the sentence when
   the body cannot. */
const apiError = (e, fallback) => {
  const status = e?.response?.status;
  const message = e?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (!status) return "Could not reach the server.";
  if (status === 401) return "Your session has expired — sign in again.";
  if (status === 403)
    return "Your account is not allowed to count this store's shelf.";
  if (status === 404) return "That store is not on the estate any more.";
  if (status === 503) return "The server is busy — try again in a moment.";
  return fallback;
};

/* how much of a long list is drawn before it turns into "download the rest".
   A 5,000-row sheet must not put 5,000 nodes on the page, but the operator
   still has to be able to SEE which rows are wrong — so every capped list
   below is paired with a download of the whole thing. */
const LIST_CAP = 200;
const SCRATCH_CAP = 50;

const MODES = [
  {
    value: "set",
    title: "Set",
    hint: "The count IS the shelf. What you type replaces whatever the system thinks is there.",
    verb: "set to",
  },
  {
    value: "add",
    title: "Add",
    hint: "A delivery landed. What you type is added on top of what is already there.",
    verb: "added to",
  },
  {
    value: "subtract",
    title: "Subtract",
    hint: "Wastage, damage, a transfer out. What you type comes off the shelf, floored at zero.",
    verb: "taken off",
  },
];

const STEPS = [
  { n: 1, title: "Store & mode" },
  { n: 2, title: "Count" },
  { n: 3, title: "Preview" },
  { n: 4, title: "Apply" },
];

/* Excel headers the reader understands, in priority order */
const CODE_KEYS = [
  "skucode",
  "sku",
  "productcode",
  "code",
  "itemcode",
  "item",
  "identifier",
  "name",
  "productname",
  "product",
  "itemname",
  "description",
];

/* Deliberately wide. Every spelling missing from this list used to become a
   SILENT column guess; the guess is now visible and correctable either way,
   but a recognised heading is still the only way to be sure, so the real
   count-sheet vocabulary belongs here. */
const QTY_KEYS = [
  "quantity",
  "qty",
  "count",
  "counted",
  "countedqty",
  "qtycounted",
  "stock",
  "closingstock",
  "closing",
  "closingqty",
  "currentstock",
  "newstock",
  "openingstock",
  "onhand",
  "onhandqty",
  "inhand",
  "instock",
  "available",
  "balance",
  "physical",
  "physicalcount",
  "physicalstock",
  "actual",
  "actualstock",
  "shelfcount",
  "packs",
  "grams",
  "units",
  "pcs",
  "pieces",
  "nos",
];

const normKey = (k) => String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/* ── THE BATCH'S IDEMPOTENCY KEY ──
   Every move the apply posts is keyed count:{store}:{takeId}:{itemId}, so
   re-sending one batch replays instead of adding it a second time — which is
   what stands between a lost reply and a double-counted "Add".
   It MUST be unique across operators and page loads. The preview's own `id`
   is a per-page counter, so two tablets counting one store would both send
   takeId "1" and the second one's write would be silently swallowed as a
   replay of the first. */
/* `window`, not `globalThis`: this project's eslint env predates es2020 and
   flags globalThis as undefined, which fails the production build. A browser
   bundle always has window, so nothing is lost. */
const newTakeId = () =>
  String(
    (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  ).replace(/-/g, "");

/* the same fold the backend matcher uses, so a code that resolves there is
   the code this screen recognises when it rewrites a line */
const squash = (v) =>
  String(v ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

/* Red, amber and the two movement colours have no semantic token in
   milliyo-theme.css, and the literals the rest of the portal uses fall to
   roughly 2.8:1 on the dark green surfaces. Scoping light and dark pairs here
   keeps them theme-aware without touching the shared stylesheet — the same
   trick, and the same danger/warn values, as BulkAllocate. */
const STOCK_CSS = `
.mly-bulkstock{
  --stk-danger:#B42318; --stk-danger-soft:rgba(180,35,24,.09);
  --stk-warn:#92400E;   --stk-warn-soft:rgba(146,64,14,.10);
  --stk-up:#166534;     --stk-up-soft:rgba(22,101,52,.10);
  --stk-down:#B42318;
}
html[data-theme="dark"] .mly-bulkstock{
  --stk-danger:#FF9A8F; --stk-danger-soft:rgba(255,154,143,.10);
  --stk-warn:#F5C36B;   --stk-warn-soft:rgba(245,195,107,.12);
  --stk-up:#7DDBA8;     --stk-up-soft:rgba(125,219,168,.12);
  --stk-down:#FF9A8F;
}
`;

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 8,
  textTransform: "uppercase",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  background: "var(--mly-surface)",
  color: "var(--mly-text)",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
};

const sectionStyle = {
  border: "1.5px solid var(--mly-border)",
  borderRadius: 14,
  background: "var(--mly-surface)",
  padding: "20px 22px",
  marginBottom: 18,
};

const primaryBtn = (disabled) => ({
  padding: "11px 24px",
  borderRadius: 10,
  border: "none",
  background: disabled ? "var(--mly-surface-3)" : "var(--mly-heading)",
  color: disabled ? "var(--mly-text-muted)" : "var(--mly-on-accent)",
  fontWeight: 700,
  fontSize: 14,
  cursor: disabled ? "not-allowed" : "pointer",
});

const ghostBtn = {
  padding: "11px 20px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  background: "var(--mly-surface-2)",
  color: "var(--mly-text-soft)",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
};

const ghostBtnDisabled = {
  ...ghostBtn,
  color: "var(--mly-text-muted)",
  cursor: "not-allowed",
  opacity: 0.55,
};

const linkBtn = {
  border: "none",
  background: "none",
  padding: 0,
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--mly-heading)",
  cursor: "pointer",
  textDecoration: "underline",
};

/* the store-picker pill already used on Store/Settings.jsx and Store/LiveView.jsx */
const SCOPE_PILL = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "7px 14px",
  borderRadius: 999,
  cursor: "pointer",
  border: `1.5px solid ${active ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
  background: active ? "var(--milliyo-forest)" : "var(--mly-surface)",
  color: active ? "#E8C96A" : "var(--mly-text-soft)",
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: "nowrap",
});

const monoCell = {
  fontFamily: "var(--mly-font-mono, monospace)",
  fontSize: 12.5,
};

const numCell = {
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  fontSize: 12.5,
};

const nf = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const num = (v) => nf.format(Number(v) || 0);

/* num() groups with commas, and a comma is this screen's own field separator.
   Anything the operator is invited to COPY back into the textarea has to be
   written plainly or it will not survive its own round trip — "25,000" parses
   back as code "701 25", quantity 0. */
const plain = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
};

/* every quantity on this screen carries its unit — a packs figure and a grams
   figure are different inventories and must never read as the same number */
const unitWord = (unit, value) =>
  unit === "packs" ? (Math.abs(Number(value) || 0) === 1 ? "pack" : "packs") : "g";

const qtyText = (value, unit) => `${num(value)} ${unitWord(unit, value)}`;

const signed = (value) => `${Number(value) > 0 ? "+" : ""}${num(value)}`;

/* A, B … Z, AA — how the operator's spreadsheet names the column they are
   being asked to confirm */
const colLetter = (index) => {
  let n = Number(index) || 0;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
};

/* ── QUANTITY PARSING ──────────────────────────────────────────────────
   The server strips every non-digit from the quantity field and reads what
   is left as a number — so "abc", "-" and an empty cell all arrive as 0, and
   under Set a 0 empties the shelf without anyone having typed a zero. This
   screen therefore refuses any quantity field carrying no digit at all and
   holds that line back as a problem instead of sending it. */
const parseQty = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const cleaned = s.replace(/[^\d.-]/g, "");
  if (!/\d/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/* Mirrors parseRow() in backend/routes/bulkStockRoutes.js: comma, tab,
   semicolon or pipe separated, or a single space before a trailing number,
   and the quantity is always the LAST field because a product name may carry
   spaces and commas while the number never does. */
const parseLine = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return null;

  const parts = s.split(/[,\t;|]/).map((x) => x.trim()).filter((x) => x !== "");
  if (parts.length >= 2) {
    return {
      code: parts.slice(0, -1).join(" ").trim(),
      quantity: parseQty(parts[parts.length - 1]),
    };
  }

  /* "CODE 40" — a name ending in a digit ("Chilli 334") still needs a real
     separator and will report as missing a quantity rather than losing its
     own last word */
  const m = s.match(/^(.*\S)\s+(-?\d+(?:\.\d+)?)$/);
  if (m) return { code: m[1].trim(), quantity: Number(m[2]) };

  return { code: s, quantity: null };
};

/* ── POOL TOTALS ───────────────────────────────────────────────────────
   summary.unitsIn / unitsOut on the API response add every delta together
   regardless of pool, which puts packs and grams into a single number. That
   figure is never rendered anywhere on this screen; the movement is
   recomputed per pool here instead, and shown as one row each. */
const poolTotals = (lines) => {
  const seed = () => ({ lines: 0, in: 0, out: 0 });
  const totals = { packs: seed(), bulk: seed() };
  (lines || []).forEach((l) => {
    const bucket = l.pool === "bulk" ? totals.bulk : totals.packs;
    const delta = Number(
      l.delta ?? (Number(l.after) || 0) - (Number(l.before) || 0)
    );
    bucket.lines += 1;
    if (delta > 0) bucket.in += delta;
    if (delta < 0) bucket.out += delta;
  });
  return totals;
};

/* what a batch of lines IS, independent of when it was built — so a fresh
   preview of an already-applied count can be recognised as the same count */
const signatureOf = (lines) =>
  (lines || [])
    .map((l) => `${l.pool}:${l.product}:${l.skuCode || ""}:${l.quantity}`)
    .sort()
    .join("|");

/* lines by the code the operator typed. The server echoes `code` back on
   every applied and failed row verbatim, and the per-paste dedupe guarantees
   one line per resolved target, so the string is a safe key. */
const indexByCode = (lines) => {
  const map = new Map();
  (lines || []).forEach((l) => {
    map.set(String(l.code), l);
    const sq = squash(l.code);
    if (sq && !map.has(sq)) map.set(sq, l);
  });
  return map;
};

const EMPTY_SHEET_MAP = { header: false, codeAt: -1, qtyAt: -1, qtyGuessed: false };

/* ── SHEET COLUMN MAPPING ──────────────────────────────────────────────
   A count sheet's columns are GUESSED, and a wrong guess is invisible in the
   result: a batch number read as a quantity looks exactly like a shelf that
   sold out. So the guess is made once, here, and then shown on screen as two
   selects the operator can correct — rather than re-guessed per row, silently,
   from "the first cell to the right with a digit in it".

   Order matters. The code column is settled first, because a product code is
   very often itself numeric ("701"), and a numeric-density scan run before
   that would happily elect it the quantity column. */
const guessColumns = (grid) => {
  if (!Array.isArray(grid) || !grid.length) return { ...EMPTY_SHEET_MAP };

  const width = grid.reduce(
    (w, r) => Math.max(w, Array.isArray(r) ? r.length : 0),
    0
  );

  const columns = new Map();
  (Array.isArray(grid[0]) ? grid[0] : []).forEach((cell, i) => {
    const key = normKey(cell);
    if (key && !columns.has(key)) columns.set(key, i);
  });

  const pick = (keys) => {
    for (let i = 0; i < keys.length; i += 1) {
      const at = columns.get(keys[i]);
      if (at !== undefined) return at;
    }
    return -1;
  };

  const codeByHeader = pick(CODE_KEYS);
  const qtyByHeader = pick(QTY_KEYS);
  const header = codeByHeader >= 0 || qtyByHeader >= 0;
  const body = header ? grid.slice(1) : grid;

  let codeAt = codeByHeader;
  if (codeAt < 0) {
    for (let c = 0; c < width; c += 1) {
      if (c === qtyByHeader) continue;
      const used = body.some((r) => String(r?.[c] ?? "").trim() !== "");
      if (used) {
        codeAt = c;
        break;
      }
    }
  }

  let qtyAt = qtyByHeader;
  if (qtyAt < 0) {
    /* only to the right of the code, and only a column that is almost
       entirely numbers. A column of dates or batch strings no longer
       qualifies just because it contains a digit. */
    let bestScore = 0;
    let bestFilled = 0;
    for (let c = codeAt + 1; c < width; c += 1) {
      let filled = 0;
      let numeric = 0;
      body.forEach((r) => {
        if (String(r?.[c] ?? "").trim() === "") return;
        filled += 1;
        if (parseQty(r[c]) !== null) numeric += 1;
      });
      if (!filled) continue;
      const score = numeric / filled;
      if (score < 0.9) continue;
      if (score > bestScore || (score === bestScore && filled > bestFilled)) {
        bestScore = score;
        bestFilled = filled;
        qtyAt = c;
      }
    }
  }

  return { header, codeAt, qtyAt, qtyGuessed: qtyByHeader < 0 && qtyAt >= 0 };
};

/* the grid, read through the mapping on screen — nothing implicit left */
const rowsFromGrid = (grid, map, fixes) => {
  if (!Array.isArray(grid) || !grid.length || !map || map.codeAt < 0) return [];
  const body = map.header ? grid.slice(1) : grid;
  const out = [];
  body.forEach((row, i) => {
    if (!Array.isArray(row)) return;
    const at = map.header ? i + 2 : i + 1; // the sheet's own row number
    const raw = String(row[map.codeAt] ?? "").trim();
    const code = fixes && fixes[at] ? fixes[at] : raw;
    if (!code) return;
    out.push({
      code,
      quantity: map.qtyAt < 0 ? null : parseQty(row[map.qtyAt]),
      at,
    });
  });
  return out;
};

const BulkStock = () => {
  const role = useSelector((state) => state.adminUser.role);
  const isSuperAdmin = Number(role) === SUPERADMIN_ROLE;

  /* ── scope ── */
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState("");
  const [storeId, setStoreId] = useState("");

  /* ── input ── */
  const [mode, setMode] = useState("set");
  const [tab, setTab] = useState("paste");
  const [pasteText, setPasteText] = useState("");
  const [excelGrid, setExcelGrid] = useState(null);
  const [excelFileName, setExcelFileName] = useState("");
  const [excelMap, setExcelMap] = useState({ ...EMPTY_SHEET_MAP });
  /* sheet row number → code the operator picked off a pack chooser */
  const [excelCodeFixes, setExcelCodeFixes] = useState({});
  const [showHeldBack, setShowHeldBack] = useState(false);
  const fileRef = useRef(null);

  /* ── preview / apply ── */
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [stale, setStale] = useState(false);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  /* setState is async, so two clicks inside one tick could both pass an
     `applying` check. The ref is the actual latch on the only write. */
  const applyingRef = useRef(false);

  /* Which preview generation is current. Bumped on every request AND on every
     invalidation, so a reply for a store or mode the operator has moved away
     from is dropped instead of repainting an applyable preview. */
  const previewSeq = useRef(0);

  /* problem code → the lines it was rewritten into, so a SECOND click on the
     pack chooser still finds them (the original name is gone by then) */
  const packChoicesRef = useRef(new Map());

  /* ── the stores this admin may count ──
     Already narrowed server-side: the whole estate for a superadmin, exactly
     their own store for anyone else. A list of one is therefore the only
     store this operator can count — pin to it and show no picker. */
  useEffect(() => {
    let alive = true;
    api
      .get("/admin/store-settings/overview")
      /* This payload is literally { data: [...], meta: {...} }, so `.data.data`
         is still the array — it is NOT a leftover Node wrapper, and dropping a
         `.data` here would break it. An account bound to no store is answered
         with an empty list, not an error. */
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setStores(list);
        if (list.length === 1) setStoreId(String(list[0].storeId));
      })
      .catch((e) => {
        if (!alive) return;
        setStores([]);
        setStoresError(
          apiError(e, "Could not load the stores you are allowed to count.")
        );
      })
      .finally(() => {
        if (alive) setStoresLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const storeById = useMemo(() => {
    const m = new Map();
    stores.forEach((s) => m.set(String(s.storeId), s));
    return m;
  }, [stores]);

  const activeStore = storeById.get(String(storeId));
  const storeName = activeStore?.name || "this store";
  const pinned = stores.length === 1;

  /* the overview returns `isActive` (Node called it `status`) and the apply
     only checks that the store EXISTS — a switched-off store accepts a full
     stock take with no warning anywhere. Say so on the pill and again above
     the count. */
  const storeIsClosed = !!activeStore && activeStore.isActive === false;

  /* name alone is not an identity: two branches can carry the same name and
     differ only by code, and this is the one control that decides where the
     write lands */
  const storeLabel = (s) =>
    s ? `${s.name}${s.code ? ` · ${s.code}` : ""}` : storeName;

  const labelForStoreId = (id) =>
    storeById.has(String(id)) ? storeLabel(storeById.get(String(id))) : storeName;

  /* ── invalidation ──
     A before/after was computed for ONE store under ONE mode. Changing either
     makes the whole preview meaningless, so it is dropped outright rather
     than left on screen — and any preview still in flight is abandoned with
     it. Editing the lines only marks it stale: the problem list has to
     survive while the operator fixes line after line from it.

     The last RESULT is never touched here. It is the only record of what was
     written and of which lines did not land, and it is self-contained, so it
     outlives every form change until the operator clears it deliberately. */
  const clearPreview = () => {
    previewSeq.current += 1;
    setPreview(null);
    setStale(false);
    setPreviewing(false);
    setError("");
    packChoicesRef.current = new Map();
  };

  /* The last result deliberately survives an edit: its failed lines are the
     thing the operator is retyping FROM. */
  const markStale = () => {
    setError("");
    if (preview) setStale(true);
  };

  const selectStore = (id) => {
    if (applying) return;
    if (String(id) === String(storeId)) return;
    setStoreId(String(id));
    clearPreview();
  };

  const selectMode = (value) => {
    if (applying) return;
    if (value === mode) return;
    setMode(value);
    clearPreview();
  };

  /* ── the rows currently in hand ──
     No local de-duplication, unlike BulkAllocate: the same pack counted twice
     with two different numbers is a real counting mistake, and the server
     names it line by line ("already counted on this paste"). Collapsing it
     here would hide it. */
  const pasteRows = useMemo(
    () =>
      pasteText.split(/\r?\n/).reduce((acc, ln, i) => {
        const parsed = parseLine(ln);
        if (parsed) acc.push({ ...parsed, at: i + 1 });
        return acc;
      }, []),
    [pasteText]
  );

  const excelRows = useMemo(
    () => rowsFromGrid(excelGrid, excelMap, excelCodeFixes),
    [excelGrid, excelMap, excelCodeFixes]
  );

  const rows = tab === "paste" ? pasteRows : excelRows;

  const goodRows = useMemo(() => rows.filter((r) => r.quantity !== null), [rows]);

  /* lines held back on this side — see parseQty. Carries the row number, so
     "200 held back" can be turned into a list of which 200. */
  const localProblems = useMemo(
    () =>
      rows
        .filter((r) => r.quantity === null)
        .map((r) => ({
          at: r.at,
          code: r.code,
          reason: "no quantity on this line",
        })),
    [rows]
  );

  const tooMany = goodRows.length > MAX_ROWS;
  /* Preview and Apply are mutually exclusive. Two writes cannot overlap
     (applyingRef), and a preview must not overlap an apply either: the two
     replies would land in either order and the screen would be showing one
     batch's result beside another batch's numbers. */
  const canPreview =
    !!storeId && goodRows.length > 0 && !tooMany && !previewing && !applying;

  /* ── Excel ──
     Read as a raw grid rather than objects. sheet_to_json's object mode always
     spends row 1 on column names, so a bare two-column sheet with no header
     line loses its first count without saying a word. */
  const dropSheet = () => {
    setExcelGrid(null);
    setExcelFileName("");
    setExcelMap({ ...EMPTY_SHEET_MAP });
    setExcelCodeFixes({});
    setShowHeldBack(false);
    clearPreview();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    /* The previous sheet stops being "the file in hand" the moment another is
       chosen. Clearing here rather than on the success path means EVERY
       failure below leaves an empty drop zone, no preview and an error —
       never last week's sheet still loaded, un-stale and applyable while the
       banner talks about this week's. */
    dropSheet();

    const reader = new FileReader();
    reader.onerror = () => {
      setError(`Could not read "${file.name}" — the browser could not open it.`);
    };
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!firstSheet) {
          setError(`"${file.name}" has no sheets in it.`);
          return;
        }
        const grid = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          blankrows: false,
          defval: "",
        });
        if (!grid.length) {
          setError(`"${file.name}" is empty.`);
          return;
        }
        const map = guessColumns(grid);
        setExcelGrid(grid);
        setExcelMap(map);
        setExcelFileName(file.name);
        if (map.codeAt < 0) {
          setError(
            "No code column could be found in that sheet — pick one below."
          );
        } else if (map.qtyAt < 0) {
          setError(
            "No quantity column could be found in that sheet — pick one below, or every row will be held back."
          );
        } else if (!rowsFromGrid(grid, map, {}).length) {
          setError(
            `No rows found in "${file.name}". Expected a code column and a quantity column — check the two selects below.`
          );
        }
      } catch (err) {
        setError(`Could not read "${file.name}" — is it a valid .xlsx?`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const setSheetColumn = (which, value) => {
    const raw = Number(value);
    const at = Number.isFinite(raw) ? raw : -1;
    const other = which === "codeAt" ? "qtyAt" : "codeAt";
    setExcelMap((prev) => ({
      ...prev,
      [which]: at,
      /* one column cannot be both the code and the count — reading the same
         cell twice would turn a quantity into its own product code. Picking a
         column that is already spoken for swaps the two, which is what the
         operator correcting a transposed pair means. */
      ...(at >= 0 && prev[other] === at ? { [other]: prev[which] } : {}),
      /* the operator has now looked at it, so it is no longer a guess */
      qtyGuessed: which === "qtyAt" ? false : prev.qtyGuessed,
    }));
    setExcelCodeFixes({});
    clearPreview();
  };

  const setSheetHeader = (checked) => {
    setExcelMap((prev) => ({ ...prev, header: !!checked }));
    setExcelCodeFixes({});
    clearPreview();
  };

  /* every column, with its heading and a sample of what is actually in it, so
     the mapping can be checked at a glance instead of trusted */
  const columnOptions = useMemo(() => {
    if (!Array.isArray(excelGrid) || !excelGrid.length) return [];
    const width = excelGrid.reduce(
      (w, r) => Math.max(w, Array.isArray(r) ? r.length : 0),
      0
    );
    const body = excelMap.header ? excelGrid.slice(1) : excelGrid;
    const out = [];
    for (let c = 0; c < width; c += 1) {
      const heading = excelMap.header
        ? String(excelGrid[0]?.[c] ?? "").trim()
        : "";
      const samples = [];
      for (let r = 0; r < body.length && samples.length < 2; r += 1) {
        const cell = String(body[r]?.[c] ?? "").trim();
        if (cell) samples.push(cell.length > 14 ? `${cell.slice(0, 14)}…` : cell);
      }
      out.push({ index: c, letter: colLetter(c), heading, samples });
    }
    return out;
  }, [excelGrid, excelMap.header]);

  const columnText = (opt) => {
    if (!opt) return "";
    const head = opt.heading ? ` · ${opt.heading}` : "";
    const eg = opt.samples.length ? ` — e.g. ${opt.samples.join(", ")}` : " — empty";
    return `${opt.letter}${head}${eg}`;
  };

  const downloadTemplate = () => {
    const template = [
      { code: "2295kg", quantity: 40 },
      { code: "300-500grams", quantity: 12 },
      { code: "701", quantity: 25000 },
    ];

    const readme = [
      { Instructions: "ONE LINE PER PACK OR PER BIN. Two columns: code and quantity." },
      { Instructions: "code = the variant SKU code for a packed product, or the product code for a bulk-milling bin." },
      { Instructions: "A product NAME also works, but only when it identifies exactly one stockable line. A product with several packs will come back asking which pack you meant." },
      { Instructions: "quantity for a variant SKU is counted in PACKS, and a pack is a whole thing — 12.5 is refused, not rounded." },
      { Instructions: "quantity for a bulk-milling bin is counted in GRAMS — 25000 means 25 kg. The mill books grams, so this screen does too. Grams may carry a decimal." },
      { Instructions: "Packs and grams are two separate inventories. Nothing on this screen ever adds them together, and nothing converts between them." },
      { Instructions: "Column headers are case-insensitive: 'Code', 'SKU', 'Product Code', 'Quantity', 'Qty', 'Count', 'On Hand' all work." },
      { Instructions: "The header row is optional. A sheet that is just code in column A and quantity in column B is read as-is." },
      { Instructions: "Whatever the headings say, the screen shows you which column it read as the code and which as the quantity, and lets you change both." },
      { Instructions: "A row with no quantity is held back and listed as a problem — it is never treated as zero." },
      { Instructions: "The same pack twice is a counting mistake: the second line is reported, not applied." },
      { Instructions: "The Set / Add / Subtract choice is made on screen, not in this sheet." },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    ws["!cols"] = [{ wch: 24 }, { wch: 14 }];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Count");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_bulk_stock_template.xlsx");
  };

  const downloadHeldBack = () => {
    if (!localProblems.length) return;
    const ws = XLSX.utils.json_to_sheet(
      localProblems.map((p) => ({
        row: p.at,
        code: p.code,
        quantity: "",
        reason: p.reason,
      }))
    );
    ws["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 14 }, { wch: 50 }];
    const readme = [
      { Instructions: "These rows were NOT sent to the server. Every one of them is missing a quantity." },
      { Instructions: "Fill in the quantity column and re-upload this sheet on the Excel tab." },
      { Instructions: "A missing quantity is never treated as zero — under Set that would empty the shelf." },
      { Instructions: "The 'row' column is the row number in the sheet you uploaded. The 'reason' column is ignored on re-upload." },
    ];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Count");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_bulk_stock_held_back.xlsx");
  };

  /* ── PREVIEW — reads only ── */
  const runPreview = async () => {
    if (!canPreview || applyingRef.current) return;

    previewSeq.current += 1;
    const seq = previewSeq.current;
    const forStore = String(storeId);
    const forMode = mode;
    const sent = goodRows;

    setPreviewing(true);
    setError("");
    try {
      const res = await api.post("/admin/bulk/stock/preview", {
        /* IN THE BODY, not a header. The service reads no X-Store-Id at all;
           omitting this is a 400 store_required for a superadmin and — worse
           — is invisible for a role 2/3 admin, who stays pinned to their own
           store and never notices the field is missing. */
        storeId: forStore,
        mode: forMode,
        rows: sent.map((r) => ({ code: r.code, quantity: r.quantity })),
      });
      /* abandoned mid-flight — the store or the mode moved on, so this
         answer describes a count nobody asked for any more */
      if (previewSeq.current !== seq) return;

      const body = res.data || {};
      const previewMode = String(body.mode || forMode);

      /* ── lines this screen refuses to promise ──
         The server projects `after` without rounding, but the pack writers in
         utils/variantStock.js floor: setPacks and givePacks both do
         Math.floor. A fractional PACK count would therefore be previewed and
         confirmed as 12.5 and written as 12. And givePacks short-circuits a
         zero add without reading the row, so a "0 packs arrived" line comes
         back as "0 → 0" and is logged as an emptied shelf.
         Neither is a number this screen can stand behind, so neither is sent:
         they are held back and named, exactly like a line with no quantity. */
      const kept = [];
      const heldBack = [];
      (Array.isArray(body.lines) ? body.lines : []).forEach((l) => {
        const q = Number(l.quantity);
        if (l.pool === "packs" && !Number.isInteger(q)) {
          heldBack.push({
            code: l.code,
            reason: `a pack is a whole thing — ${num(
              q
            )} packs cannot be shelved. Round it and preview again.`,
          });
          return;
        }
        if (l.pool === "packs" && previewMode === "add" && q === 0) {
          heldBack.push({
            code: l.code,
            reason:
              "adds 0 packs — nothing to write, so this line is left out rather than logged as a movement",
          });
          return;
        }
        kept.push(l);
      });

      setPreview({
        id: seq,
        /* minted with the preview, not with the write, so a retry of the same
           confirmed batch carries the same key and replays */
        takeId: newTakeId(),
        store: String(body.store || forStore),
        mode: previewMode,
        summary: body.summary || {},
        lines: kept,
        problems: Array.isArray(body.problems) ? body.problems : [],
        /* frozen with the preview so the lists always describe the same run,
           even while the operator edits the box underneath */
        heldBack,
        localProblems,
        rows: sent,
        signature: signatureOf(kept),
      });
      setStale(false);
    } catch (e) {
      if (previewSeq.current !== seq) return;
      /* The previous preview is NOT thrown away here. A backend hiccup must
         not cost the operator the problem list they were working through —
         they would be left with strictly less than they had before pressing
         the button. It is stale or it is not; the banner says which. */
      setError(apiError(e, "Could not preview this stock take"));
    } finally {
      if (previewSeq.current === seq) setPreviewing(false);
    }
  };

  /* ── APPLY — the only write ── */
  const runApply = async () => {
    if (applyingRef.current || previewing) return;
    const batch = preview;
    if (!batch || !batch.lines.length || stale) return;
    /* the invariant kept locally rather than left to the render gating: one
       preview is written at most once, so a second confirm cannot double it */
    if (result && result.previewId === batch.id) return;

    applyingRef.current = true;
    setConfirming(false);
    setApplying(true);
    setError("");
    try {
      const res = await api.post("/admin/bulk/stock/apply", {
        /* the store, mode and take id the preview was COMPUTED under, echoed
           back by the server — never the current state of the controls. The
           store is a body field now; there is no X-Store-Id header. */
        storeId: batch.store,
        mode: batch.mode,
        takeId: batch.takeId,
        ...(note.trim() ? { note: note.trim() } : {}),
        lines: batch.lines,
      });
      /* A RESULT IS SELF-CONTAINED. It carries the lines that produced it, so
         the failed list and the applied table read this snapshot and never
         the live `preview` — which by now may be a different batch entirely,
         or may have been cleared by Start over while the POST was in flight.
         Nothing below this line can make a completed write unrenderable. */
      setResult({
        data: res.data || {},
        previewId: batch.id,
        lines: batch.lines,
        mode: batch.mode,
        store: batch.store,
        signature: batch.signature,
        at: Date.now(),
      });
    } catch (e) {
      setError(apiError(e, "The stock take could not be applied"));
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  /* ── what has already been written ── */
  const previewApplied =
    !!result && !!preview && result.previewId === preview.id;

  /* the same count, previewed again after it landed. The server's replay
     guard is keyed on the take id, and every preview mints its own — so a
     second preview of the same lines is a second batch, and applying it in
     Add mode still adds the count twice. */
  const alreadyApplied =
    !!preview &&
    !!result &&
    !previewApplied &&
    result.signature === preview.signature &&
    result.mode === preview.mode &&
    result.store === preview.store &&
    preview.lines.length > 0;

  const previewTotals = useMemo(() => poolTotals(preview?.lines), [preview]);

  const appliedTotals = useMemo(
    () => poolTotals(result?.data?.applied),
    [result]
  );

  const allProblems = useMemo(() => {
    if (!preview) return [];
    return [
      ...(preview.localProblems || []),
      ...(preview.heldBack || []),
      ...(preview.problems || []),
    ];
  }, [preview]);

  /* ── the applied table, reconciled ──
     The preview's before/after and the apply's before/after are two separate
     reads and this screen used to render both as equally authoritative. They
     can legitimately differ (something sold in between), and they can differ
     because of a defect further down the stack. Either way the operator is
     entitled to see WHICH lines did not land on the number they confirmed. */
  const appliedRows = useMemo(() => {
    const byCode = indexByCode(result?.lines);
    return (result?.data?.applied || []).map((a) => {
      const line = byCode.get(String(a.code)) || byCode.get(squash(a.code));
      const promised = line ? Number(line.after) : null;
      const actual = Number(a.after) || 0;
      return {
        ...a,
        promised,
        promisedBefore: line ? Number(line.before) : null,
        /* NEW: the server reports wrote:false for a line the take id's replay
           guard swallowed. It is listed among the applied with a projected
           after, but nothing moved for it in this batch — and under Add that
           projection is the count applied a second time on paper only. It is
           not a mismatch (the numbers are consistent), so it needs saying
           separately or it is indistinguishable from a real write. */
        replayed: a.wrote === false,
        mismatch:
          promised !== null && Math.abs(promised - actual) > 0.0000001,
      };
    });
  }, [result]);

  const mismatchCount = appliedRows.filter((a) => a.mismatch).length;
  const replayedCount = appliedRows.filter((a) => a.replayed).length;

  /* quantities for a code, off the frozen preview rows */
  const rowsForCode = (code) => {
    const target = squash(code);
    return (preview?.rows || []).filter((r) => squash(r.code) === target);
  };

  /* The chooser keeps each line's OWN counted number. It never lends one line's
     count to another — a line with no quantity stays without one and is held
     back, which is the same rule parseQty enforces everywhere else. */
  const qtyHintFor = (code) => {
    const hits = rowsForCode(code);
    if (!hits.length) return "";
    const uniq = [...new Set(hits.map((h) => h.quantity))];
    if (uniq.length === 1 && uniq[0] !== null) return ` — keeps ${num(uniq[0])}`;
    return " — each line keeps its own count";
  };

  /* a problem carrying packs[] is a product name that matches several packs.
     Clicking one rewrites every line that used that name, in place, so the
     fix happens without leaving the screen.

     A MISCLICK MUST BE FIXABLE. The first click replaces the name with a SKU,
     so matching on the name alone would find nothing on the second click and
     the chip would be a silent no-op. Every rewrite is therefore remembered —
     which rows it touched and what it wrote — and a later click on the same
     problem's chips finds those rows again. */
  const choosePack = (problemCode, skuCode) => {
    const target = squash(problemCode);
    const prior = packChoicesRef.current.get(target);
    const priorCode = prior ? squash(prior.code) : "";

    const isTargetRow = (rowCode, at) => {
      const sq = squash(rowCode);
      if (sq === target) return true;
      return !!prior && !!priorCode && sq === priorCode && prior.rows.includes(at);
    };

    const touched = [];

    if (tab === "paste") {
      const next = pasteText
        .split(/\r?\n/)
        .map((ln, i) => {
          const parsed = parseLine(ln);
          if (!parsed || !isTargetRow(parsed.code, i + 1)) return ln;
          touched.push(i + 1);
          /* no quantity in, no quantity out — the line stays held back
             instead of being handed a number nobody counted for it */
          return parsed.quantity === null
            ? skuCode
            : `${skuCode}, ${plain(parsed.quantity)}`;
        })
        .join("\n");
      setPasteText(next);
    } else {
      excelRows.forEach((r) => {
        if (isTargetRow(r.code, r.at)) touched.push(r.at);
      });
      if (touched.length) {
        setExcelCodeFixes((prev) => {
          const next = { ...prev };
          touched.forEach((at) => {
            next[at] = skuCode;
          });
          return next;
        });
      }
    }

    if (touched.length) {
      packChoicesRef.current.set(target, { code: skuCode, rows: touched });
    }
    markStale();
  };

  /* everything the apply refused, as lines that can go straight back in.
     Keyed against the SNAPSHOT the result carries, never the live preview. */
  const failedLines = useMemo(() => {
    const byCode = indexByCode(result?.lines);
    return (result?.data?.failed || []).map((f) => {
      const line = byCode.get(String(f.code)) || byCode.get(squash(f.code));
      return {
        code: f.code,
        quantity: line ? line.quantity : "",
        reason: f.reason || "Failed",
      };
    });
  }, [result]);

  const failedText = useMemo(
    () =>
      failedLines
        .map((f) => (f.quantity === "" ? f.code : `${f.code}, ${plain(f.quantity)}`))
        .join("\n"),
    [failedLines]
  );

  const repasteFailures = () => {
    if (!failedText) return;
    const text = failedText;
    setTab("paste");
    dropSheet();
    setPasteText(text);
  };

  const downloadFailed = () => {
    if (!failedLines.length) return;
    const ws = XLSX.utils.json_to_sheet(
      failedLines.map((f) => ({
        code: f.code,
        quantity: f.quantity,
        reason: f.reason,
      }))
    );
    ws["!cols"] = [{ wch: 24 }, { wch: 14 }, { wch: 60 }];
    const readme = [
      { Instructions: "These lines were NOT applied. Fix the code or the quantity and re-upload this sheet on the Excel tab." },
      { Instructions: "The 'reason' column is ignored on re-upload — leave it or delete it." },
      { Instructions: "Re-run it in the SAME mode you used, or the numbers will land differently." },
    ];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Count");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_bulk_stock_failed.xlsx");
  };

  const resetAll = () => {
    setConfirmingReset(false);
    setPasteText("");
    setExcelGrid(null);
    setExcelFileName("");
    setExcelMap({ ...EMPTY_SHEET_MAP });
    setExcelCodeFixes({});
    setShowHeldBack(false);
    setNote("");
    previewSeq.current += 1;
    setPreview(null);
    setStale(false);
    setPreviewing(false);
    setResult(null);
    setError("");
    packChoicesRef.current = new Map();
  };

  /* Start over throws away the last write's failed list, which is the only
     record of which lines did not land — nothing else persists it. One
     mis-click must not be able to lose it. */
  const startOver = () => {
    if (applying) return;
    if (failedLines.length > 0) {
      setConfirmingReset(true);
      return;
    }
    resetAll();
  };

  const activeMode = MODES.find((m) => m.value === mode) || MODES[0];
  const previewMode =
    MODES.find((m) => m.value === preview?.mode) || activeMode;
  const resultMode = MODES.find((m) => m.value === result?.mode) || activeMode;

  const applyCount = preview?.lines?.length || 0;
  /* the other half of the interlock: a write may not start while a preview is
     in flight either, or the two answers overlap again from this side */
  const canApply =
    !!preview &&
    !previewApplied &&
    applyCount > 0 &&
    !stale &&
    !applying &&
    !previewing;
  const applySentence = `Apply ${applyCount} line${
    applyCount === 1 ? "" : "s"
  } to ${labelForStoreId(preview?.store)}`;

  const resultAtText = result?.at
    ? new Date(result.at).toLocaleTimeString([], { timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  /* ── which step is live ── */
  const showApplyBlock = !!preview && !previewApplied;
  const currentStep = showApplyBlock
    ? 3
    : result
    ? 4
    : storeId && goodRows.length
    ? 2
    : 1;
  const stepReached = (n) => {
    if (n === 1) return true;
    if (n === 2) return !!storeId;
    if (n === 3) return !!preview || !!result;
    if (n === 4) return !!result;
    return false;
  };

  /* ── small pieces ── */
  const countTile = (label, value, color, sub) => (
    <div
      style={{
        flex: 1,
        minWidth: 140,
        border: "1.5px solid var(--mly-border)",
        borderRadius: 12,
        background: "var(--mly-surface-2)",
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          fontSize: 24,
          fontWeight: 800,
          color,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "var(--mly-text-muted)",
          marginTop: 4,
        }}
      >
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );

  const poolPill = (pool) => (
    <span
      style={{
        display: "inline-block",
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        background:
          pool === "bulk" ? "var(--mly-surface-3)" : "var(--mly-accent-soft)",
        color: pool === "bulk" ? "var(--mly-accent)" : "var(--mly-heading)",
        border: "1px solid var(--mly-border)",
      }}
    >
      {pool === "bulk" ? "Bulk · g" : "Packs"}
    </span>
  );

  /* one row per pool, never a combined line */
  const poolTotalRow = (key, totals) => {
    const t = totals[key];
    if (!t.lines) return null;
    const unit = key === "packs" ? "packs" : "bulk";
    return (
      <div
        key={key}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          padding: "8px 0",
          fontSize: 12.5,
          color: "var(--mly-text-soft)",
        }}
      >
        {poolPill(key)}
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {t.lines} line{t.lines === 1 ? "" : "s"}
        </span>
        <span style={{ color: "var(--stk-up)", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
          +{qtyText(t.in, unit)} in
        </span>
        <span style={{ color: "var(--stk-down)", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
          {qtyText(t.out, unit)} out
        </span>
      </div>
    );
  };

  const stepHead = (n, title, sub, badge) => (
    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 800,
          background: "var(--mly-accent-soft)",
          color: "var(--mly-heading)",
          border: "1px solid var(--mly-border)",
        }}
      >
        {n}
      </div>
      <div>
        <h6
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 15,
            color: "var(--mly-heading)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {title}
          {badge}
        </h6>
        {sub && (
          <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>{sub}</span>
        )}
      </div>
    </div>
  );

  const chip = (text, tone) => (
    <span
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: 999,
        background:
          tone === "warn" ? "var(--stk-warn-soft)" : "var(--mly-surface-3)",
        color: tone === "warn" ? "var(--stk-warn)" : "var(--mly-text-soft)",
        border: `1px solid ${
          tone === "warn" ? "var(--stk-warn)" : "var(--mly-border)"
        }`,
      }}
    >
      {text}
    </span>
  );

  const readOnlyBadge = chip("Read only");

  const tabButton = (key, label) => (
    <div
      key={key}
      onClick={() => {
        if (tab === key || applying) return;
        setTab(key);
        clearPreview();
      }}
      style={{
        padding: "9px 18px",
        borderRadius: 10,
        cursor: applying ? "not-allowed" : "pointer",
        fontSize: 13,
        fontWeight: 700,
        userSelect: "none",
        opacity: applying && tab !== key ? 0.6 : 1,
        border:
          tab === key
            ? "2px solid var(--mly-heading)"
            : "1.5px solid var(--mly-border)",
        background: tab === key ? "var(--mly-accent-soft)" : "var(--mly-surface-2)",
        color: tab === key ? "var(--mly-heading)" : "var(--mly-text-muted)",
        transition: "all 0.15s",
      }}
    >
      {label}
    </div>
  );

  const banner = (text, tone) => (
    <div
      style={{
        background:
          tone === "warn" ? "var(--stk-warn-soft)" : "var(--stk-danger-soft)",
        color: tone === "warn" ? "var(--stk-warn)" : "var(--stk-danger)",
        border: `1.5px solid ${
          tone === "warn" ? "var(--stk-warn)" : "var(--stk-danger)"
        }`,
        padding: "10px 14px",
        borderRadius: 8,
        marginBottom: 16,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  );

  /* an account bound to no store gets an empty list from the overview, and
     every call it could make here would come back 403 — say so up front */
  const noStores = !storesLoading && stores.length === 0;

  return (
    <div className="mly-bulkstock">
      <style>{STOCK_CSS}</style>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 style={{ margin: 0, fontWeight: 700 }}>Bulk Stock</h5>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                A stock take for one store. Paste what you counted, check the
                preview, then apply — packs and grams stay separate throughout.
              </span>
            </div>
            {/* never live during a write: clearing the form mid-POST used to
                make the completed batch's result unrenderable */}
            <button
              className="btn btn-primary btn-sm widthbtn"
              onClick={startOver}
              disabled={applying}
              title={
                applying
                  ? "Wait for the count being applied to finish"
                  : "Clear the form"
              }
            >
              {applying ? "Applying…" : "Start over"}
            </button>
          </div>

          <div className="card-body">
            {/* ── step rail ── */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 20,
              }}
            >
              {STEPS.map((s) => {
                const done = stepReached(s.n) && currentStep > s.n;
                const live = currentStep === s.n && stepReached(s.n);
                return (
                  <div
                    key={s.n}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 14px",
                      borderRadius: 20,
                      fontSize: 12.5,
                      fontWeight: 700,
                      border:
                        live || done
                          ? "1.5px solid var(--mly-heading)"
                          : "1.5px solid var(--mly-border)",
                      background: live
                        ? "var(--mly-accent-soft)"
                        : "var(--mly-surface-2)",
                      color:
                        live || done
                          ? "var(--mly-heading)"
                          : "var(--mly-text-muted)",
                    }}
                  >
                    <span>{done ? "✓" : s.n}</span>
                    <span>{s.title}</span>
                  </div>
                );
              })}
            </div>

            {storesError && banner(storesError)}
            {noStores &&
              !storesError &&
              banner(
                "Your account is not assigned to a store, so there is no shelf to count. Ask a super admin to assign one."
              )}
            {error && banner(error)}
            {storeIsClosed &&
              banner(
                `${storeLabel(
                  activeStore
                )} is switched off. A count can still be written to it, but nothing there is on sale — check you have the right branch.`,
                "warn"
              )}

            {/* ══ STEP 1 — STORE & MODE ══ */}
            <div style={sectionStyle}>
              {stepHead(
                1,
                "Store & mode",
                "A stock take always belongs to exactly one store — there is no all-stores count."
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Store being counted</label>

                {storesLoading ? (
                  <div style={{ fontSize: 13, color: "var(--mly-text-muted)" }}>
                    Loading stores…
                  </div>
                ) : pinned ? (
                  <>
                    <div
                      style={{
                        ...SCOPE_PILL(true),
                        cursor: "default",
                        gap: 8,
                      }}
                    >
                      {storeLabel(stores[0])}
                      {stores[0].isActive === false ? " · closed" : ""}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--mly-text-muted)",
                        marginTop: 8,
                      }}
                    >
                      {isSuperAdmin
                        ? "The only store on the estate — every count lands here."
                        : "Your account is bound to this store, so this count can only land here."}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      {stores.map((s) => {
                        const active = String(s.storeId) === String(storeId);
                        const closed = s.isActive === false;
                        return (
                          <button
                            key={s.storeId}
                            type="button"
                            onClick={() => selectStore(s.storeId)}
                            disabled={applying}
                            style={{
                              ...SCOPE_PILL(active),
                              ...(applying
                                ? { cursor: "not-allowed", opacity: 0.6 }
                                : {}),
                              ...(closed && !active
                                ? { borderStyle: "dashed" }
                                : {}),
                            }}
                          >
                            {/* name alone is not an identity — two branches can
                                share one, and this control decides where the
                                write lands */}
                            {storeLabel(s)}
                            {s.isDefault ? " · Main" : ""}
                            {closed ? " · closed" : ""}
                          </button>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: storeId
                          ? "var(--mly-text-muted)"
                          : "var(--stk-warn)",
                        fontWeight: storeId ? 400 : 700,
                      }}
                    >
                      {storeId
                        ? `Every line below will be counted against ${storeLabel(
                            activeStore
                          )}. Switching store clears the preview.`
                        : "Pick the store you are counting. Nothing can be previewed until you do."}
                    </div>
                  </>
                )}
              </div>

              <label style={labelStyle}>What the count does to the shelf</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                  gap: 10,
                }}
              >
                {MODES.map((m) => {
                  const sel = mode === m.value;
                  return (
                    <div
                      key={m.value}
                      onClick={() => selectMode(m.value)}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        cursor: applying ? "not-allowed" : "pointer",
                        userSelect: "none",
                        opacity: applying && !sel ? 0.6 : 1,
                        border: sel
                          ? "2px solid var(--mly-heading)"
                          : "1.5px solid var(--mly-border)",
                        background: sel
                          ? "var(--mly-accent-soft)"
                          : "var(--mly-surface-2)",
                        transition: "all 0.15s",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: sel
                            ? "var(--mly-heading)"
                            : "var(--mly-text-soft)",
                        }}
                      >
                        {m.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--mly-text-muted)",
                          marginTop: 4,
                          lineHeight: 1.45,
                        }}
                      >
                        {m.hint}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ══ STEP 2 — THE COUNT ══ */}
            <div style={sectionStyle}>
              {stepHead(
                2,
                "The count",
                "One line per pack or per bin: the code, then how many."
              )}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                {tabButton("paste", "Paste")}
                {tabButton("excel", "Excel / CSV")}
              </div>

              {tab === "paste" ? (
                <div>
                  <label style={labelStyle}>
                    Code, quantity — one line each
                    <span
                      style={{
                        fontWeight: 500,
                        letterSpacing: 0,
                        textTransform: "none",
                        color: "var(--mly-text-muted)",
                        marginLeft: 6,
                      }}
                    >
                      (a pack SKU counts in whole packs, a bulk bin counts in grams)
                    </span>
                  </label>
                  <textarea
                    value={pasteText}
                    onChange={(e) => {
                      setPasteText(e.target.value);
                      markStale();
                    }}
                    rows={10}
                    placeholder={"2295kg, 40\n300-500grams, 12\n701\t25000\nSharbati Wheat Flour 1kg, 8"}
                    style={{
                      ...inputStyle,
                      fontFamily: "var(--mly-font-mono, monospace)",
                      fontSize: 13,
                      resize: "vertical",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--mly-text-muted)",
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    Comma, semicolon, pipe, tab or a single space all separate
                    the code from the number — so two columns copied straight
                    out of Excel paste in as they are. The quantity is always
                    the last thing on the line.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div
                      onClick={() => fileRef.current && fileRef.current.click()}
                      style={{
                        flex: 1,
                        minWidth: 260,
                        border: "2px dashed var(--mly-heading)",
                        borderRadius: 12,
                        background: excelRows.length
                          ? "var(--mly-surface)"
                          : "var(--mly-accent-soft)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 110,
                        cursor: "pointer",
                        padding: 12,
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 22 }}>📄</div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--mly-text-soft)",
                        }}
                      >
                        {excelFileName || "UPLOAD A COUNT SHEET"}
                      </span>
                      <span
                        style={{ fontSize: 11, color: "var(--mly-text-muted)" }}
                      >
                        {excelFileName
                          ? `${excelRows.length} row${
                              excelRows.length === 1 ? "" : "s"
                            } read`
                          : "XLSX, XLS or CSV — a code column and a quantity column, or just those two columns with no headings"}
                      </span>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                      />
                    </div>

                    <div
                      style={{
                        minWidth: 220,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <button onClick={downloadTemplate} style={ghostBtn}>
                        Download template
                      </button>
                      <span
                        style={{ fontSize: 11.5, color: "var(--mly-text-muted)" }}
                      >
                        The template carries a READ ME tab covering both pools
                        and their units.
                      </span>
                    </div>
                  </div>

                  {/* ── which columns were read ──
                      The column choice used to be a silent per-row guess, and a
                      batch number read as a quantity is invisible in the
                      result: under Subtract it just looks like shelves going to
                      zero. So the guess is shown, with a sample of what is in
                      each column, and can be corrected. */}
                  {excelGrid && (
                    <div
                      style={{
                        marginTop: 12,
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 12,
                        background: "var(--mly-surface-2)",
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          flexWrap: "wrap",
                          marginBottom: 10,
                        }}
                      >
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                          How this sheet is being read
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={excelMap.header}
                            onChange={(e) => setSheetHeader(e.target.checked)}
                          />
                          First row is a heading
                        </label>
                      </div>

                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 240 }}>
                          <label style={labelStyle}>Code column</label>
                          <select
                            value={String(excelMap.codeAt)}
                            onChange={(e) =>
                              setSheetColumn("codeAt", e.target.value)
                            }
                            style={{ ...inputStyle, fontSize: 13 }}
                          >
                            <option value="-1">— none —</option>
                            {columnOptions.map((c) => (
                              <option key={`code-${c.index}`} value={String(c.index)}>
                                {columnText(c)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ flex: 1, minWidth: 240 }}>
                          <label style={labelStyle}>Quantity column</label>
                          <select
                            value={String(excelMap.qtyAt)}
                            onChange={(e) =>
                              setSheetColumn("qtyAt", e.target.value)
                            }
                            style={{ ...inputStyle, fontSize: 13 }}
                          >
                            <option value="-1">— none —</option>
                            {columnOptions.map((c) => (
                              <option key={`qty-${c.index}`} value={String(c.index)}>
                                {columnText(c)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {excelMap.qtyGuessed && excelMap.qtyAt >= 0 && (
                        <div style={{ marginTop: 10 }}>
                          {banner(
                            `No quantity heading was recognised. Column ${colLetter(
                              excelMap.qtyAt
                            )} was chosen because it is the only column of numbers to the right of the code — check it is the count and not a batch number, a price or a date.`,
                            "warn"
                          )}
                        </div>
                      )}
                      {excelMap.qtyAt < 0 && (
                        <div style={{ marginTop: 10 }}>
                          {banner(
                            "No quantity column is selected, so every row is held back. Pick the column holding the counted number.",
                            "warn"
                          )}
                        </div>
                      )}
                      {excelMap.codeAt < 0 && (
                        <div style={{ marginTop: 10 }}>
                          {banner(
                            "No code column is selected, so nothing can be matched. Pick the column holding the SKU, product code or name.",
                            "warn"
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {excelRows.length > 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 10,
                        background: "var(--mly-surface-2)",
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: "var(--mly-text-soft)",
                        fontFamily: "var(--mly-font-mono, monospace)",
                        maxHeight: 180,
                        overflowY: "auto",
                      }}
                    >
                      {excelRows.slice(0, SCRATCH_CAP).map((r) => (
                        <div key={`x-${r.at}`}>
                          <span style={{ color: "var(--mly-text-muted)" }}>
                            {r.at}.{" "}
                          </span>
                          {r.code} ,{" "}
                          {r.quantity === null ? (
                            <span style={{ color: "var(--stk-warn)" }}>
                              no quantity
                            </span>
                          ) : (
                            num(r.quantity)
                          )}
                        </div>
                      ))}
                      {excelRows.length > SCRATCH_CAP && (
                        <div style={{ color: "var(--mly-text-muted)" }}>
                          … +{excelRows.length - SCRATCH_CAP} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── ready / held back ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  flexWrap: "wrap",
                  marginTop: 14,
                  fontSize: 12.5,
                }}
              >
                <span
                  style={{
                    color: "var(--mly-text-soft)",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {goodRows.length} line{goodRows.length === 1 ? "" : "s"} ready
                </span>
                {localProblems.length > 0 && (
                  <>
                    <span
                      style={{
                        color: "var(--stk-warn)",
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {localProblems.length} held back — no quantity on the line
                    </span>
                    {/* the preview cannot be taken when nothing is ready, so
                        the preview's problem list is not reachable in the one
                        case where the operator most needs to see WHICH rows
                        are wrong. This list does not depend on it. */}
                    <button
                      type="button"
                      onClick={() => setShowHeldBack((v) => !v)}
                      style={linkBtn}
                    >
                      {showHeldBack ? "Hide them" : `Show the ${localProblems.length}`}
                    </button>
                    <button type="button" onClick={downloadHeldBack} style={linkBtn}>
                      Download them
                    </button>
                  </>
                )}
              </div>

              {showHeldBack && localProblems.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    border: "1.5px solid var(--stk-warn)",
                    borderRadius: 12,
                    background: "var(--stk-warn-soft)",
                    padding: "8px 14px",
                    maxHeight: 260,
                    overflowY: "auto",
                    fontSize: 12.5,
                    color: "var(--mly-text-soft)",
                  }}
                >
                  {localProblems.slice(0, LIST_CAP).map((p, i) => (
                    <div key={`held-${p.at}-${i}`} style={{ padding: "3px 0" }}>
                      <span
                        style={{
                          ...numCell,
                          display: "inline-block",
                          minWidth: 42,
                          textAlign: "left",
                          color: "var(--mly-text-muted)",
                        }}
                      >
                        {tab === "paste" ? "line" : "row"} {p.at}
                      </span>{" "}
                      <span style={{ ...monoCell, fontWeight: 700 }}>
                        {p.code || "—"}
                      </span>
                      <span style={{ color: "var(--mly-text-muted)" }}>
                        {" "}
                        — {p.reason}
                      </span>
                    </div>
                  ))}
                  {localProblems.length > LIST_CAP && (
                    <div
                      style={{
                        color: "var(--mly-text-muted)",
                        padding: "6px 0 2px",
                      }}
                    >
                      … +{localProblems.length - LIST_CAP} more — download the
                      list to see every one.
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "var(--mly-text-muted)",
                      paddingTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    A missing quantity is never read as zero — under Set that
                    would empty the shelf. Give each of these a number, or take
                    them out.
                  </div>
                </div>
              )}

              {tooMany &&
                banner(
                  `Too many rows: ${goodRows.length}. Send at most ${MAX_ROWS} at a time.`,
                  "warn"
                )}

              <div style={{ marginTop: 16 }}>
                <button
                  onClick={runPreview}
                  disabled={!canPreview}
                  style={primaryBtn(!canPreview)}
                >
                  {previewing
                    ? "Reading the shelf…"
                    : applying
                    ? "Applying a count…"
                    : "Preview the count"}
                </button>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--mly-text-muted)",
                    marginTop: 10,
                    lineHeight: 1.5,
                  }}
                >
                  {applying
                    ? "A count is being written right now. Previewing is held until it finishes, so the two answers cannot be mistaken for each other."
                    : "Preview only reads. It changes nothing at all — not one pack, not one gram — until you press Apply in step 4."}
                </div>
              </div>
            </div>

            {/* ══ STEP 3 — PREVIEW ══
                Only while it is still a preview. Once these lines have been
                written, every reassurance in this block ("Read only", "Nothing
                has been written", "the shelf is untouched") is false, and an
                operator who scrolls up and reads it concludes the batch did
                not go through. */}
            {showApplyBlock && (
              <div style={sectionStyle}>
                {stepHead(
                  3,
                  "Preview",
                  `${previewMode.title} — every counted quantity is ${
                    previewMode.verb
                  } the shelf at ${labelForStoreId(
                    preview.store
                  )}. Nothing has been written.`,
                  readOnlyBadge
                )}

                {stale &&
                  banner(
                    "The lines changed since this preview was taken. Preview again before applying — these before/after numbers no longer describe what you would write.",
                    "warn"
                  )}

                {alreadyApplied &&
                  banner(
                    `These are the same ${preview.lines.length} lines that were already applied at ${resultAtText} in ${resultMode.title} mode. Applying them again writes them a second time — in Add mode that doubles every one of them.`,
                    "warn"
                  )}

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  {countTile(
                    "Lines matched",
                    preview.lines.length,
                    "var(--mly-heading)"
                  )}
                  {countTile(
                    "Problems",
                    allProblems.length,
                    allProblems.length
                      ? "var(--stk-danger)"
                      : "var(--mly-text-muted)"
                  )}
                  {countTile(
                    "Pack lines",
                    previewTotals.packs.lines,
                    "var(--mly-accent)",
                    "counted in packs"
                  )}
                  {countTile(
                    "Bulk lines",
                    previewTotals.bulk.lines,
                    "var(--mly-accent)",
                    "counted in grams"
                  )}
                </div>

                {/* ── movement, one row per pool ── */}
                <div
                  style={{
                    border: "1.5px solid var(--mly-border)",
                    borderRadius: 12,
                    background: "var(--mly-surface-2)",
                    padding: "6px 14px 10px",
                    marginBottom: 18,
                  }}
                >
                  {poolTotalRow("packs", previewTotals)}
                  {poolTotalRow("bulk", previewTotals)}
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "var(--mly-text-muted)",
                      lineHeight: 1.5,
                      paddingTop: 4,
                    }}
                  >
                    Packs and grams are two independent inventories. They are
                    never added together and nothing here converts between them.
                  </div>
                </div>

                {/* ── the lines ── */}
                {preview.lines.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>
                      What will change ({preview.lines.length})
                    </label>
                    <div
                      style={{
                        overflowX: "auto",
                        maxHeight: 420,
                        overflowY: "auto",
                      }}
                    >
                      <table className="display table table-bordered">
                        <thead>
                          <tr>
                            <th style={{ minWidth: 44 }}>#</th>
                            <th style={{ minWidth: 130 }}>You typed</th>
                            <th style={{ minWidth: 200 }}>Product</th>
                            <th style={{ minWidth: 120 }}>Pack</th>
                            <th style={{ minWidth: 88 }}>Pool</th>
                            {/* the number that was READ off the line. Without
                                it a mis-read column is invisible: under
                                Subtract it just looks like a shelf emptying. */}
                            <th style={{ minWidth: 96, textAlign: "right" }}>
                              Counted
                            </th>
                            <th style={{ minWidth: 96, textAlign: "right" }}>
                              Before
                            </th>
                            <th style={{ minWidth: 96, textAlign: "right" }}>
                              After
                            </th>
                            <th style={{ minWidth: 96, textAlign: "right" }}>
                              Delta
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.lines.map((l, i) => {
                            const delta = Number(l.delta) || 0;
                            const color =
                              delta > 0
                                ? "var(--stk-up)"
                                : delta < 0
                                ? "var(--stk-down)"
                                : "var(--mly-text-muted)";
                            return (
                              <tr key={`${l.product}-${l.skuCode}-${i}`}>
                                <td style={numCell}>{i + 1}</td>
                                <td style={monoCell}>
                                  {l.code || "—"}
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: "var(--mly-text-muted)",
                                      fontFamily: "inherit",
                                    }}
                                  >
                                    by {l.matchedOn}
                                  </div>
                                </td>
                                <td style={{ fontSize: 13 }}>
                                  {l.productName || "—"}
                                </td>
                                <td
                                  style={{
                                    fontSize: 12.5,
                                    color: "var(--mly-text-soft)",
                                  }}
                                >
                                  {l.pool === "packs"
                                    ? l.label || l.skuCode || "—"
                                    : "Bulk bin"}
                                  {l.pool === "packs" && l.label && l.skuCode ? (
                                    <div
                                      style={{
                                        ...monoCell,
                                        fontSize: 10.5,
                                        color: "var(--mly-text-muted)",
                                      }}
                                    >
                                      {l.skuCode}
                                    </div>
                                  ) : null}
                                </td>
                                <td>{poolPill(l.pool)}</td>
                                <td
                                  style={{
                                    ...numCell,
                                    fontWeight: 700,
                                    color: "var(--mly-text-soft)",
                                  }}
                                >
                                  {qtyText(l.quantity, l.unit)}
                                </td>
                                <td
                                  style={{
                                    ...numCell,
                                    color: "var(--mly-text-muted)",
                                  }}
                                >
                                  {qtyText(l.before, l.unit)}
                                </td>
                                <td style={{ ...numCell, fontWeight: 700 }}>
                                  {qtyText(l.after, l.unit)}
                                </td>
                                <td style={{ ...numCell, color, fontWeight: 700 }}>
                                  {signed(delta)} {unitWord(l.unit, delta)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── problems ── */}
                {allProblems.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>
                      Not counted ({allProblems.length}) — each with its reason
                    </label>
                    <div
                      style={{
                        border: "1.5px solid var(--stk-danger)",
                        borderRadius: 12,
                        background: "var(--stk-danger-soft)",
                        padding: "6px 14px",
                        maxHeight: 320,
                        overflowY: "auto",
                      }}
                    >
                      {allProblems.slice(0, LIST_CAP).map((p, i) => (
                        <div
                          key={`prob-${i}`}
                          style={{
                            padding: "9px 0",
                            borderBottom:
                              i === Math.min(allProblems.length, LIST_CAP) - 1
                                ? "none"
                                : "1px solid var(--mly-border-soft)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                              alignItems: "baseline",
                            }}
                          >
                            {p.at ? (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--mly-text-muted)",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {tab === "paste" ? "line" : "row"} {p.at}
                              </span>
                            ) : null}
                            <span
                              style={{
                                ...monoCell,
                                fontWeight: 700,
                                color: "var(--mly-text-soft)",
                              }}
                            >
                              {p.code || "—"}
                            </span>
                            <span
                              style={{
                                fontSize: 12.5,
                                color: "var(--mly-text-soft)",
                              }}
                            >
                              {p.reason}
                            </span>
                          </div>

                          {/* a name that matches several packs — pick one and
                              the line is rewritten in place. Clicking a second
                              time corrects a misclick: the rewrite is
                              remembered, so the chips keep working. */}
                          {Array.isArray(p.packs) && p.packs.length > 0 && (
                            <div style={{ marginTop: 7 }}>
                              <div
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  textTransform: "uppercase",
                                  color: "var(--mly-text-muted)",
                                  marginBottom: 5,
                                }}
                              >
                                Pick the pack you counted
                                {qtyHintFor(p.code)}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 7,
                                  flexWrap: "wrap",
                                }}
                              >
                                {/* `sku`, not `skuCode` — the server sends the
                                    pack chooser's rows as { sku, label }. The
                                    chip renders either way (it falls back to
                                    the label), so reading the wrong field is
                                    silent: it writes "undefined" into the
                                    paste box on click. */}
                                {p.packs.map((pk, pi) => (
                                  <button
                                    key={`pk-${i}-${pi}`}
                                    type="button"
                                    onClick={() => choosePack(p.code, pk.sku)}
                                    style={{
                                      border: "1.5px solid var(--mly-border)",
                                      background: "var(--mly-surface)",
                                      color: "var(--mly-text-soft)",
                                      borderRadius: 999,
                                      padding: "5px 12px",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: "pointer",
                                    }}
                                  >
                                    {pk.label || pk.sku}
                                    <span
                                      style={{
                                        ...monoCell,
                                        fontSize: 10.5,
                                        color: "var(--mly-text-muted)",
                                        marginLeft: 6,
                                      }}
                                    >
                                      {pk.sku}
                                    </span>
                                  </button>
                                ))}
                              </div>
                              <div
                                style={{
                                  fontSize: 10.5,
                                  color: "var(--mly-text-muted)",
                                  marginTop: 5,
                                }}
                              >
                                Picked the wrong one? Click another — the line
                                is rewritten again.
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {allProblems.length > LIST_CAP && (
                        <div
                          style={{
                            padding: "9px 0",
                            fontSize: 12.5,
                            color: "var(--mly-text-muted)",
                          }}
                        >
                          … +{allProblems.length - LIST_CAP} more not counted.
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--mly-text-muted)",
                        marginTop: 7,
                        lineHeight: 1.5,
                      }}
                    >
                      These lines are simply left out — the rest still apply.
                      Fix them above and preview again to bring them in.
                    </div>
                  </div>
                )}

                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--mly-text-muted)",
                    background: "var(--mly-surface-2)",
                    border: "1.5px solid var(--mly-border)",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  Still a preview. The shelf is untouched until you confirm in
                  step 4 — and the numbers are re-read at that moment, so a pack
                  that sells in between still lands correctly. Whatever is
                  actually written is reported line by line below.
                </div>
              </div>
            )}

            {/* ══ STEP 4 — APPLY ══
                The result half stands on its own: it renders from the snapshot
                the write returned, so it survives Start over, a new preview and
                a cleared form. The apply half appears only for a preview that
                has not been written yet. */}
            {(showApplyBlock || result) && (
              <div style={sectionStyle}>
                {stepHead(
                  4,
                  "Apply",
                  "This writes stock and stamps the audit trail."
                )}

                {showApplyBlock && (
                  <div style={{ marginBottom: result ? 22 : 0 }}>
                    <div style={{ maxWidth: 560, marginBottom: 16 }}>
                      <label style={labelStyle}>
                        Note
                        <span
                          style={{
                            fontWeight: 500,
                            letterSpacing: 0,
                            textTransform: "none",
                            color: "var(--mly-text-muted)",
                            marginLeft: 6,
                          }}
                        >
                          (saved on every line's stock log)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={note}
                        maxLength={200}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. Monday morning shelf count — Priya"
                        style={inputStyle}
                      />
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--mly-text-muted)",
                          marginTop: 5,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <span>
                          Say who counted and why — this is what anyone reading
                          the stock log later has to go on.
                        </span>
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {note.length}/200
                        </span>
                      </div>
                    </div>

                    {alreadyApplied &&
                      banner(
                        `Careful — this is the same count that was applied at ${resultAtText}. There is no undo and no duplicate check on the server.`,
                        "warn"
                      )}

                    <button
                      onClick={() => setConfirming(true)}
                      disabled={!canApply}
                      style={primaryBtn(!canApply)}
                    >
                      {applying ? "Applying…" : applySentence}
                    </button>
                  </div>
                )}

                {result && (
                  <div>
                    {showApplyBlock && (
                      <div
                        style={{
                          borderTop: "1.5px solid var(--mly-border)",
                          paddingTop: 18,
                          marginBottom: 14,
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: "var(--mly-text-muted)",
                        }}
                      >
                        THE LAST COUNT THAT WAS WRITTEN
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                        marginBottom: 12,
                        fontSize: 12.5,
                        color: "var(--mly-text-soft)",
                      }}
                    >
                      {chip("Written")}
                      <span>
                        {resultMode.title} · {labelForStoreId(result.store)}
                        {resultAtText ? ` · ${resultAtText}` : ""}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 16,
                      }}
                    >
                      {countTile(
                        "Applied",
                        result.data?.summary?.applied ??
                          (result.data?.applied || []).length,
                        "var(--stk-up)"
                      )}
                      {countTile(
                        "Failed",
                        result.data?.summary?.failed ??
                          (result.data?.failed || []).length,
                        (result.data?.failed || []).length
                          ? "var(--stk-danger)"
                          : "var(--mly-text-muted)"
                      )}
                    </div>

                    {appliedRows.length > 0 && (
                      <div
                        style={{
                          border: "1.5px solid var(--mly-border)",
                          borderRadius: 12,
                          background: "var(--mly-surface-2)",
                          padding: "6px 14px 10px",
                          marginBottom: 18,
                        }}
                      >
                        {poolTotalRow("packs", appliedTotals)}
                        {poolTotalRow("bulk", appliedTotals)}
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--mly-text-muted)",
                            paddingTop: 4,
                          }}
                        >
                          What actually moved, counted in each pool's own unit.
                        </div>
                      </div>
                    )}

                    {/* ── the preview and the write, reconciled ──
                        Two separate reads of the same shelf. Rendering both as
                        equally authoritative and never comparing them is how a
                        contradiction goes unnoticed, so the disagreements are
                        counted and named. */}
                    {mismatchCount > 0 &&
                      banner(
                        `${mismatchCount} line${
                          mismatchCount === 1 ? "" : "s"
                        } did not land on the number the preview promised — they are marked below. The After column is what the shelf holds now. Check those lines on the stock console before trusting this count.`,
                        "warn"
                      )}

                    {/* A replay is not a write. The server reports the line,
                        with its projected after, but the movement was already
                        on record under this batch's take id and nothing moved
                        for it now — so it must not be read as a fresh count. */}
                    {replayedCount > 0 &&
                      banner(
                        `${replayedCount} line${
                          replayedCount === 1 ? "" : "s"
                        } had already been written under this batch and were not moved again — they are marked "replay" below. Nothing was double-counted.`,
                        "warn"
                      )}

                    {appliedRows.length > 0 && (
                      <div style={{ marginBottom: 18 }}>
                        <label style={labelStyle}>
                          Applied ({appliedRows.length})
                        </label>
                        <div
                          style={{
                            overflowX: "auto",
                            maxHeight: 300,
                            overflowY: "auto",
                          }}
                        >
                          <table className="display table table-bordered">
                            <thead>
                              <tr>
                                <th style={{ minWidth: 130 }}>Code</th>
                                <th style={{ minWidth: 200 }}>Product</th>
                                <th style={{ minWidth: 88 }}>Pool</th>
                                <th style={{ minWidth: 96, textAlign: "right" }}>
                                  Before
                                </th>
                                <th style={{ minWidth: 96, textAlign: "right" }}>
                                  After
                                </th>
                                <th style={{ minWidth: 110, textAlign: "right" }}>
                                  Preview said
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {appliedRows.map((a, i) => {
                                const unit = a.pool === "bulk" ? "g" : "packs";
                                return (
                                  <tr key={`ap-${i}`}>
                                    <td style={monoCell}>{a.code || "—"}</td>
                                    <td style={{ fontSize: 13 }}>
                                      {a.productName || "—"}
                                      {a.skuCode ? (
                                        <div
                                          style={{
                                            ...monoCell,
                                            fontSize: 10.5,
                                            color: "var(--mly-text-muted)",
                                          }}
                                        >
                                          {a.skuCode}
                                        </div>
                                      ) : null}
                                    </td>
                                    <td>{poolPill(a.pool)}</td>
                                    <td
                                      style={{
                                        ...numCell,
                                        color: "var(--mly-text-muted)",
                                      }}
                                    >
                                      {qtyText(a.before, unit)}
                                    </td>
                                    <td
                                      style={{
                                        ...numCell,
                                        fontWeight: 700,
                                        color: a.mismatch
                                          ? "var(--stk-warn)"
                                          : "inherit",
                                      }}
                                    >
                                      {qtyText(a.after, unit)}
                                    </td>
                                    <td
                                      style={{
                                        ...numCell,
                                        color: a.mismatch
                                          ? "var(--stk-warn)"
                                          : "var(--mly-text-muted)",
                                        fontWeight: a.mismatch ? 700 : 400,
                                      }}
                                    >
                                      {a.replayed
                                        ? "replay"
                                        : a.promised === null
                                        ? "—"
                                        : a.mismatch
                                        ? `≠ ${qtyText(a.promised, unit)}`
                                        : "same"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {failedLines.length > 0 && (
                      <div style={{ marginBottom: 18 }}>
                        <label style={labelStyle}>
                          Failed ({failedLines.length}) — kept here so you can
                          re-run them
                        </label>
                        <div
                          style={{
                            border: "1.5px solid var(--stk-danger)",
                            borderRadius: 12,
                            background: "var(--stk-danger-soft)",
                            padding: "8px 14px",
                            maxHeight: 220,
                            overflowY: "auto",
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                          }}
                        >
                          {failedLines.slice(0, LIST_CAP).map((f, i) => (
                            <div key={`fail-${i}`} style={{ padding: "3px 0" }}>
                              {/* written plainly, not through num(): this text
                                  is meant to be selected and pasted back into
                                  the box, and a grouping comma is this
                                  screen's own field separator */}
                              <span style={{ ...monoCell, fontWeight: 700 }}>
                                {f.code}
                                {f.quantity === "" ? "" : `, ${plain(f.quantity)}`}
                              </span>
                              <span style={{ color: "var(--mly-text-muted)" }}>
                                {" "}
                                — {f.reason}
                              </span>
                            </div>
                          ))}
                          {failedLines.length > LIST_CAP && (
                            <div
                              style={{
                                color: "var(--mly-text-muted)",
                                padding: "6px 0 2px",
                              }}
                            >
                              … +{failedLines.length - LIST_CAP} more — download
                              them to see every one.
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: "var(--mly-text-muted)",
                            marginTop: 7,
                          }}
                        >
                          Re-run them in <strong>{resultMode.title}</strong> mode
                          — the same mode this batch used — or the numbers will
                          land differently.
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {failedLines.length > 0 && (
                        <button
                          onClick={repasteFailures}
                          disabled={applying}
                          style={applying ? ghostBtnDisabled : ghostBtn}
                        >
                          Re-paste the {failedLines.length} failure
                          {failedLines.length === 1 ? "" : "s"}
                        </button>
                      )}
                      {failedLines.length > 0 && (
                        <button onClick={downloadFailed} style={ghostBtn}>
                          Download failed rows
                        </button>
                      )}
                      <button
                        onClick={startOver}
                        disabled={applying}
                        style={primaryBtn(applying)}
                      >
                        Count something else
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── confirm the write ── */}
      {confirming && preview && !previewApplied && (
        <>
          <div
            onClick={() => setConfirming(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 480,
              background: "var(--mly-surface)",
              borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 32px",
            }}
          >
            <h2 style={{ margin: "0 0 14px", fontSize: 20, fontWeight: 700 }}>
              {applySentence}?
            </h2>
            <div
              style={{
                fontSize: 14,
                color: "var(--mly-text-soft)",
                lineHeight: 1.6,
                marginBottom: 14,
              }}
            >
              Every counted quantity is {previewMode.verb} the shelf at{" "}
              <strong>{labelForStoreId(preview.store)}</strong>. This is the
              write.
            </div>

            {alreadyApplied && (
              <div style={{ marginBottom: 6 }}>
                {banner(
                  `This exact count was already written at ${resultAtText} in ${resultMode.title} mode. Confirming writes it a second time.`,
                  "warn"
                )}
              </div>
            )}

            <div
              style={{
                border: "1.5px solid var(--mly-border)",
                borderRadius: 12,
                background: "var(--mly-surface-2)",
                padding: "6px 14px 10px",
                marginBottom: 20,
              }}
            >
              {poolTotalRow("packs", previewTotals)}
              {poolTotalRow("bulk", previewTotals)}
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setConfirming(false)} style={ghostBtn}>
                Cancel
              </button>
              <button
                onClick={runApply}
                disabled={applying}
                style={{ ...primaryBtn(applying), flex: 1 }}
              >
                {applying ? "Applying…" : "Yes, apply the count"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── confirm throwing the last result away ──
          The failed list is the only copy of which lines did not land. */}
      {confirmingReset && (
        <>
          <div
            onClick={() => setConfirmingReset(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 460,
              background: "var(--mly-surface)",
              borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 32px",
            }}
          >
            <h2 style={{ margin: "0 0 14px", fontSize: 20, fontWeight: 700 }}>
              Discard the {failedLines.length} failed line
              {failedLines.length === 1 ? "" : "s"}?
            </h2>
            <div
              style={{
                fontSize: 14,
                color: "var(--mly-text-soft)",
                lineHeight: 1.6,
                marginBottom: 20,
              }}
            >
              Nothing that was written is undone — only this record of what did
              not land. It is the only copy, and there is no way to get it back
              short of re-running the whole count. Download it first if you have
              not.
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button onClick={() => setConfirmingReset(false)} style={ghostBtn}>
                Keep it
              </button>
              <button onClick={downloadFailed} style={ghostBtn}>
                Download first
              </button>
              <button
                onClick={resetAll}
                style={{ ...primaryBtn(false), flex: 1 }}
              >
                Discard and start over
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkStock;
